import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Star, Plus, Trash2, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useUpload } from '../../../contexts/UploadContext';
import { useParams } from 'react-router-dom';

interface ExistingImage {
    id: number;
    url: string;
    is_main: boolean;
}

interface MediaStepProps {
    t: any;
    mainImageFile: File | null;
    galleryFiles: File[];
    existingImages: ExistingImage[];
}

export interface MediaStepRef {
    getData: () => {
        mainImageFile: File | null;
        galleryFiles: File[];
        existingImages: ExistingImage[];
        deletedMediaIds: number[];
    };
}

const MediaStep = forwardRef<MediaStepRef, MediaStepProps>(({
    t,
    mainImageFile: initialMain,
    galleryFiles: initialGallery,
    existingImages: initialExisting
}, ref) => {
    const { id: propertyId } = useParams();
    const { uploadFiles } = useUpload();

    const [localMain, setLocalMain] = useState<File | null>(initialMain);
    const [localGallery, setLocalGallery] = useState<File[]>(initialGallery);
    const [localExisting, setLocalExisting] = useState<ExistingImage[]>(initialExisting);
    const [deletedIds, setDeletedIds] = useState<number[]>([]);

    // Sync local state when initialExisting changes (essential for edit flow)
    useEffect(() => {
        setLocalExisting(initialExisting);
    }, [initialExisting]);

    useImperativeHandle(ref, () => ({
        getData: () => ({
            mainImageFile: localMain,
            galleryFiles: localGallery,
            existingImages: localExisting,
            deletedMediaIds: deletedIds
        })
    }));

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, type: 'main' | 'gallery') => {
        if (e.target.files && e.target.files.length > 0) {
            const filesArray = Array.from(e.target.files);

            if (type === 'main') {
                setLocalMain(filesArray[0]);
            } else {
                setLocalGallery(prev => [...prev, ...filesArray]);
            }
        }
    };

    const removeLocalFile = (index: number, type: 'main' | 'gallery') => {
        if (type === 'main') {
            setLocalMain(null);
        } else {
            setLocalGallery(prev => prev.filter((_, i) => i !== index));
        }
    };

    const removeExistingImage = (id: number) => {
        if (window.confirm(t('common.confirm_delete'))) {
            setLocalExisting((prev: ExistingImage[]) => prev.filter((img: ExistingImage) => img.id !== id));
            setDeletedIds(prev => [...prev, id]);
        }
    };

    return (
        <div className="space-y-8 animate-fade-in">
            <h3 className="text-lg font-bold text-gray-800 border-b pb-2 mb-4">{t('property_form.media')}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Main Image */}
                <div className="space-y-3">
                    <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <Star className="w-4 h-4 text-primary" />
                        {t('property_form.main_image')}
                    </h4>
                    <div className="relative group aspect-video bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 overflow-hidden hover:border-primary/30 transition-all">
                        {localMain || localExisting.find(img => img.is_main) ? (
                            <>
                                <img
                                    src={localMain ? URL.createObjectURL(localMain) : localExisting.find(img => img.is_main)?.url}
                                    alt="Main"
                                    className="w-full h-full object-cover"
                                />
                                <button
                                    onClick={() => localMain ? removeLocalFile(0, 'main') : removeExistingImage(localExisting.find(img => img.is_main)!.id)}
                                    className="absolute top-2 right-2 p-2 bg-white/90 text-red-500 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-all hover:bg-red-50"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </>
                        ) : (
                            <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer">
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleFileSelect(e, 'main')} />
                                <ImageIcon className="w-8 h-8 text-gray-300 mb-2" />
                                <span className="text-xs text-gray-400">{t('property_form.click_to_upload')}</span>
                            </label>
                        )}
                    </div>
                </div>

                {/* Sub Images / Gallery */}
                <div className="space-y-3">
                    <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                        <Plus className="w-4 h-4 text-primary" />
                        {t('property_form.sub_images') || 'الصور الفرعية'}
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                        {/* Existing Gallery Images */}
                        {localExisting.filter(img => !img.is_main).map((img) => (
                            <div key={img.id} className="relative group aspect-square bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                <img src={img.url} alt="Sub" className="w-full h-full object-cover" />
                                <button
                                    onClick={() => removeExistingImage(img.id)}
                                    className="absolute top-1 right-1 p-1 bg-white/90 text-red-500 rounded-md shadow-sm opacity-0 group-hover:opacity-100 transition-all"
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        ))}

                        {/* Local Gallery Previews */}
                        {localGallery.map((file, idx) => (
                            <div key={idx} className="relative group aspect-square bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                <img src={URL.createObjectURL(file)} alt="Local Sub" className="w-full h-full object-cover" />
                                <button
                                    onClick={() => removeLocalFile(idx, 'gallery')}
                                    className="absolute top-1 right-1 p-1 bg-white/90 text-red-500 rounded-md shadow-sm opacity-0 group-hover:opacity-100 transition-all"
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        ))}

                        {(localExisting.filter(img => !img.is_main).length + localGallery.length) < 10 && (
                            <label className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-all">
                                <input type="file" multiple className="hidden" accept="image/*" onChange={(e) => handleFileSelect(e, 'gallery')} />
                                <Plus className="w-6 h-6 text-gray-300" />
                            </label>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4 bg-primary/5 border border-primary/10 rounded-xl text-primary text-sm flex items-center gap-3">
                <CloudUpload className="w-5 h-5 opacity-60" />
                <span>
                    {t('property_form.media_local_hint') || 'سيتم رفع جميع الصور التي تختارها بمجرد الضغط على زر "إنهاء" أو "حفظ" في الخطوة الأخيرة.'}
                </span>
            </div>
        </div>
    );
});

const CloudUpload = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.5 19c2.5 0 4.5-2 4.5-4.5 0-2-1.5-3.5-3.5-4h-.1a5.5 5.5 0 0 0-10.8 0h-.1c-2 .5-3.5 2-3.5 4 0 2.5 2 4.5 4.5 4.5h10z" /><path d="M12 11v8" /><path d="m9 14 3-3 3 3" /></svg>
);

export default MediaStep;
